/** @jsxImportSource @emotion/react */
import React from "react";
import { useEffectOnce } from "usehooks-ts";
import { OrderCompletedBottomSection } from "./OrderCompletedBottomSection";
import { YouReceiveSection } from "../../sections/YouReceiveSection";
import { GlobalStore } from "../../../../store/global/GlobalStore";
import { getBalances } from "../../../../store/global/getBalances";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { Divider } from "antd";
import { calculateUsdTradeSurplus } from "../../../../utils/orderUtils";
import { Amount } from "../../../../types/amount";
import { AMOUNT_TO_SHOW_TRADE_SURPLUS } from "../../../../config/consts";
import { getPermit2Allowances } from "store/global/getPermit2Allowance";

export interface OrderCompletedProps {}

export const OrderCompleted: React.FC<OrderCompletedProps> = () => {
  const tokenList = useTokenListByChain();
  const { network, address, order, quote } = GlobalStore.useState(s => {
    return {
      network: s.network,
      address: s.address,
      order: s.order,
      quote: s.quote,
    };
  });
  const surplus = calculateUsdTradeSurplus(order, quote);

  useEffectOnce(() => {
    if (tokenList) {
      void getBalances.run({ address: address as `0x${string}`, tokenList, chain: network });
      void getPermit2Allowances.run({ address: address as `0x${string}`, tokenList });
    }
  });

  return (
    <OrderCompletedContainer
      key={5}
      variants={variants}
      initial="enter"
      animate="animate"
      custom={surplus}
      exit="exit"
    >
      <YouReceiveSection />
      <DividerContainer>
        <StyledDivider dashed={true} />
      </DividerContainer>
      <OrderCompletedBottomSection />
    </OrderCompletedContainer>
  );
};
export const OrderCompletedContainer = styled(motion.div)`
  max-width: 392px;
  width: 100%;
  min-height: 604px;
  border-radius: 36px;
  margin: 32px auto auto;
  position: relative;
  max-height: 604px;
  height: 100%;
  perspective: 500px;
`;

export const DividerContainer = styled.div`
  background: ${props => props.theme["Dark mode"]["1700"]};
  display: flex;
  height: 42px;
  width: 100%;
  clip-path: polygon(101% -1%, 95% 50%, 101% 101%, -1% 101%, 5% 50%, -1% -1%);
`;

const StyledDivider = styled(Divider)`
  margin: auto;
  border-width: 1px;
  border-color: #3c5659;
`;

const variants = {
  enter: (surplus: Amount) => {
    if (surplus.value.gt(AMOUNT_TO_SHOW_TRADE_SURPLUS)) {
      return {
        rotateY: -180,
      };
    } else {
      return {
        y: 50,
        opacity: 0,
      };
    }
  },
  animate: (surplus: Amount) => {
    if (surplus.value.gt(AMOUNT_TO_SHOW_TRADE_SURPLUS)) {
      return {
        rotateY: 0,
        transition: {
          rotateY: { duration: 0.5 },
        },
      };
    } else {
      return {
        y: 0,
        opacity: 1,
      };
    }
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      y: direction < 0 ? 50 : -50,
      opacity: 0,
    };
  },
};
